import { useTranslation } from 'react-i18next'
import {
  Button,
  ResponsiveRender,
  Text,
  Box,
  Columns,
  Column,
  IconCodeBranch
} from '@machobear/design-system'
import { Link } from 'react-router-dom'
import { breakpoints } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { TabMenu } from '@/components/tabs'
import SearchBar from '@/components/search-bar'
import { FilterBar, FilterTextWrapper } from '@/components/search-filter'
import { handleTabByKeyboard } from '@/utils/handleTabByKeyboard'
import MyListingsTabs from './my-listings-tabs'
import ListingTableHeader from './my-listings-header-wrapper'
import LogoIcon from './logo.svg?react'
import * as styles from './index.css'

const CreateListingButton = () => {
  const { t } = useTranslation()
  return (
    <Link to="/listing/create">
      <Button
        id="application-create-new-listing-click"
        variant="solid"
        state="default"
        tone="default"
        size="standard"
        label="Create new listing"
      >
        {t('l.listing.list.create_new_listing.button', 'Create new listing')}
      </Button>
    </Link>
  )
}

const MyListingsHeader = ({
  tabCount,
  currentTab,
  setCurrentTab,
  currentListingTab,
  setCurrentListingTab,
  onSearch,
  searchString,
  setSearchString
}) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const displayBelowDesktop = useMediaQuery(`(max-width: ${breakpoints.desktop})`)
  const isIntegrationTabSelected = currentListingTab === 'integrations'
  const isLivrentTabSelected = currentListingTab === 'livrent'

  const listingTabItems = [
    {
      key: 'livrent',
      onClick: () => {
        return setCurrentListingTab('livrent')
      },
      isActive: isLivrentTabSelected,
      children: (
        <Text
          align="center"
          size="small"
          icon={<LogoIcon />}
          weight={isLivrentTabSelected ? 'strong' : 'regular'}
        >
          {t('l.listings.tab.livrent.text', 'liv.rent')}
        </Text>
      )
    },
    {
      key: 'integrations',
      onClick: () => {
        return setCurrentListingTab('integrations')
      },
      isActive: isIntegrationTabSelected,
      children: (
        <Text
          align="center"
          size="small"
          icon={<IconCodeBranch />}
          weight={isIntegrationTabSelected ? 'strong' : 'regular'}
        >
          {t('l.listings.tab.integrations.text', 'Integrations')}
        </Text>
      )
    }
  ]

  const menuItemsForLivrent = [
    {
      key: 'draft',
      onClick: () => setCurrentTab('draft'),
      isActive: currentTab === 'draft',
      children: (
        <FilterTextWrapper>
          {t('l.listing.list.draft.button', 'Draft')} <span>({tabCount.draft})</span>
        </FilterTextWrapper>
      )
    },
    {
      key: 'published',
      onClick: () => setCurrentTab('published'),
      isActive: currentTab === 'published',
      children: (
        <FilterTextWrapper>
          {t('l.listing.list.published.button', 'Published')} <span>({tabCount.published})</span>
        </FilterTextWrapper>
      )
    },
    {
      key: 'hidden',
      onClick: () => setCurrentTab('hidden'),
      isActive: currentTab === 'hidden',
      children: (
        <FilterTextWrapper>
          {t('l.listing.list.hidden.button', 'Hidden')} <span>({tabCount.hidden})</span>
        </FilterTextWrapper>
      )
    }
  ]

  const menuItemsForIntegrations = menuItemsForLivrent.filter(menu => menu.key !== 'draft')

  const tabItems = {
    livrent: menuItemsForLivrent,
    integrations: menuItemsForIntegrations
  }

  const menuItems = tabItems[currentListingTab]

  const onSearchWithScroll = value => {
    const output = onSearch(value)
    if (typeof window !== 'undefined') window.scrollTo(0, 0)
    return output
  }

  return (
    <>
      <ListingTableHeader
        title={t('l.listing.list.listings.title', 'Listings')}
        onSearch={onSearch}
        isIntegrationTabSelected={isIntegrationTabSelected}
      >
        <MyListingsTabs
          options={listingTabItems}
          theme={displayMobile ? 'primary' : 'secondary'}
          size="medium"
          setCurrentTab={setCurrentListingTab}
        />
        <Box
          id={`tab-panel-${currentListingTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${currentListingTab}`}
          display="flex"
          justifyContent="center"
          flexDirection="row"
          background="white"
          paddingTop={{ mobile: 'none', tablet: 'xlarge' }}
          padding={{ mobile: 'xsmall', tablet: 'none' }}
        >
          <Box
            background="white"
            style={{
              maxWidth: '1130px',
              margin: displayBelowDesktop ? '0 30px' : '0 50px',
              flex: 1
            }}
            tabIndex={0}
            onKeyDown={e => handleTabByKeyboard(e, menuItems, currentTab, setCurrentTab)}
          >
            <Columns space="none" collapseBelow="tablet">
              <Column width={isLivrentTabSelected ? '1/2' : '2/3'}>
                <FilterBar>
                  <TabMenu
                    options={menuItems}
                    theme={displayMobile ? 'primary' : 'secondary'}
                    size="medium"
                    height={displayMobile && 44}
                    tabIndex={-1}
                  />
                </FilterBar>
              </Column>
              <Column width={isLivrentTabSelected ? '1/2' : '1/3'} align="right">
                <Box width="full">
                  <Box className={styles.searchInnerStyle}>
                    <ResponsiveRender above="mobile">
                      <Box
                        height="touchable"
                        display="flex"
                        gap="gutter"
                        alignItems="center"
                        justifyContent="flexEnd"
                        width="full"
                      >
                        {isLivrentTabSelected && <CreateListingButton />}
                        <SearchBar
                          placeholder={t('common.search', 'Search')}
                          onSearch={onSearchWithScroll}
                          fullWidth
                          searchString={searchString}
                          setSearchString={setSearchString}
                        />
                      </Box>
                    </ResponsiveRender>
                    <ResponsiveRender below="tablet">
                      <Columns space="small">
                        <Column width="5/5">
                          <SearchBar
                            placeholder={t('common.search', 'Search')}
                            onSearch={onSearchWithScroll}
                            fullWidth
                            searchString={searchString}
                            setSearchString={setSearchString}
                          />
                        </Column>
                      </Columns>
                    </ResponsiveRender>
                  </Box>
                </Box>
              </Column>
            </Columns>
          </Box>
        </Box>
      </ListingTableHeader>
    </>
  )
}

export default MyListingsHeader
